import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Calendar as CalendarIcon, Mail, UserPlus, X } from "lucide-react"
import { listDrafts, removeDraft, subscribeDrafts, type AnyDraft } from "@/lib/draftModals"

/**
 * Floating bar rechts unten — zeigt alle minimierten Modal-Entwuerfe.
 * Klick auf eine Pill → Navigation zur passenden Seite mit `?restore_draft=<id>`.
 * Die Ziel-Komponente (z.B. CalendarView, EmailDetail) liest den Parameter,
 * holt den Draft aus dem Store und oeffnet das Modal vorausgefuellt.
 */
export function MinimizedDraftsBar() {
  const navigate = useNavigate()
  const [drafts, setDrafts] = useState<AnyDraft[]>(() => listDrafts())

  useEffect(() => {
    const unsub = subscribeDrafts(() => setDrafts(listDrafts()))
    return unsub
  }, [])

  if (drafts.length === 0) return null

  const goto = (d: AnyDraft) => {
    if (d.kind === "calendar") {
      const q = new URLSearchParams({ restore_draft: d.id })
      if (d.tenant) q.set("tenant", d.tenant)
      navigate(`/calendar?${q.toString()}`)
    } else if (d.kind === "mail") {
      const q = new URLSearchParams({ restore_draft: d.id })
      if (d.tenant) q.set("tenant", d.tenant)
      navigate(`/emails?${q.toString()}`)
    } else if (d.kind === "contact") {
      // 🔴 Bei Mails MUSS es die Liste sein, nicht `/emails/<id>`.
      // Angelegt wird der Entwurf im Kontakt-Dialog der Smart Inbox; die
      // Detailseite kennt weder den Dialog noch den Wiederherstell-Hook.
      // Vorher landete man dort auf der Mail, und der Entwurf blieb liegen —
      // genau Marlenes „die mail geht auf" + „den entwurf kann ich nicht
      // weiterbearbeiten" (Ticket #9).
      const q = new URLSearchParams({ restore_draft: d.id })
      if (d.source === "email") {
        q.set("open", String(d.sourceId))
        navigate(`/emails?${q.toString()}`)
      } else {
        const target = d.source === "meiti" ? `/calls/${d.sourceId}` : `/sipgate/${d.sourceId}`
        navigate(`${target}?${q.toString()}`)
      }
    }
  }

  return (
    <div
      style={{
        position: "fixed", bottom: 16, right: 16, zIndex: 520,
        display: "flex", flexDirection: "column", gap: 8,
        alignItems: "flex-end",
      }}
    >
      {drafts.map((d) => (
        <div
          key={d.id}
          onClick={() => goto(d)}
          style={{
            cursor: "pointer",
            background: "#fff", border: "2px solid #6366f1", borderRadius: 12,
            boxShadow: "0 8px 24px rgba(99,102,241,0.25)",
            padding: "10px 14px", minWidth: 260, maxWidth: 360,
            fontSize: 13,
            display: "flex", alignItems: "center", gap: 10,
            transition: "transform .12s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)" }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "none" }}
        >
          <div
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {d.kind === "calendar"
              ? <CalendarIcon className="w-4 h-4 text-white" />
              : d.kind === "mail"
                ? <Mail className="w-4 h-4 text-white" />
                : <UserPlus className="w-4 h-4 text-white" />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Entwurf: {d.kind === "calendar" ? "Termin" : d.kind === "mail" ? "E-Mail" : "Kontakt"}
            </div>
            <div
              style={{
                fontWeight: 600, color: "#0f172a",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
              title={draftLabel(d)}
            >
              {draftLabel(d)}
            </div>
            <div
              style={{
                fontSize: 12, color: "#64748b",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {draftSubLabel(d)}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (confirm("Entwurf verwerfen?")) removeDraft(d.id)
            }}
            title="Entwurf verwerfen"
            style={{
              background: "transparent", color: "#94a3b8",
              border: "1px solid #e2e8f0", borderRadius: 6,
              padding: "4px 8px", cursor: "pointer", fontSize: 14,
            }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}

function draftLabel(d: AnyDraft): string {
  if (d.kind === "calendar") return d.title || "(ohne Titel)"
  if (d.kind === "mail") return d.subject || "(ohne Betreff)"
  const n = [d.first_name, d.last_name].filter(Boolean).join(" ")
  return n || d.email || d.phone || "(neuer Kontakt)"
}

function draftSubLabel(d: AnyDraft): string {
  if (d.kind === "calendar") {
    try {
      return new Date(d.start).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    } catch { return d.start }
  }
  if (d.kind === "mail") return d.to ? `an ${d.to}` : "noch ohne Empfänger"
  return d.source === "email" ? "aus E-Mail"
       : d.source === "meiti" ? "aus Meiti-Anruf"
       : "aus Sipgate-Anruf"
}
